/**
 * Environment Configuration
 *
 * Type-safe access to environment variables with validation.
 * All VITE_ prefixed variables are exposed to the client.
 */

interface EnvironmentConfig {
  // Azure AD
  azure: {
    clientId: string;
    tenantId: string;
    redirectUri: string;
  };
  // Dataverse
  dataverse: {
    url: string;
    apiVersion: string;
  };
  // Development
  dev: {
    enableDevTools: boolean;
    logLevel: 'debug' | 'info' | 'warn' | 'error';
    mockApi: boolean;
  };
  // Runtime
  isProduction: boolean;
  isDevelopment: boolean;
}

function getEnvVar(key: string, required: boolean = true): string {
  const raw = import.meta.env[key];
  const value = typeof raw === 'string' ? raw : undefined;

  if (required && !value) {
    throw new Error(
      `Missing required environment variable: ${key}. ` +
        `Please check your .env.local file.`
    );
  }

  return value ?? '';
}

function createConfig(): EnvironmentConfig {
  const isProduction = import.meta.env.PROD;
  const isDevelopment = import.meta.env.DEV;

  return {
    azure: {
      // These are no longer used by the frontend (BFF handles auth server-side)
      // but kept for backward compatibility. Not required.
      clientId: getEnvVar('VITE_AZURE_CLIENT_ID', false),
      tenantId: getEnvVar('VITE_AZURE_TENANT_ID', false),
      redirectUri: getEnvVar('VITE_AZURE_REDIRECT_URI', false),
    },
    dataverse: {
      url: getEnvVar('VITE_DATAVERSE_URL'),
      apiVersion: getEnvVar('VITE_DATAVERSE_API_VERSION', false) || 'v9.2',
    },
    dev: {
      enableDevTools: getEnvVar('VITE_ENABLE_DEV_TOOLS', false) === 'true',
      logLevel: (getEnvVar('VITE_LOG_LEVEL', false) || 'info') as EnvironmentConfig['dev']['logLevel'],
      mockApi: getEnvVar('VITE_MOCK_API', false) === 'true',
    },
    isProduction,
    isDevelopment,
  };
}

// Singleton config instance
export const env = createConfig();

// Type augmentation for Vite's ImportMeta. Mirrors the shape Vite injects at
// build time (PROD/DEV/MODE/SSR/BASE_URL) plus the VITE_* vars this repo uses.
// The string index signature lets getEnvVar() do dynamic key lookups.
declare global {
  interface ImportMetaEnv {
    // Vite built-ins (not `readonly` so we don't conflict with Vite's own
    // vite/client declarations when that ambient type is included)
    PROD: boolean;
    DEV: boolean;
    MODE: string;
    SSR: boolean;
    BASE_URL: string;
    readonly VITE_AZURE_CLIENT_ID: string;
    readonly VITE_AZURE_TENANT_ID: string;
    readonly VITE_AZURE_REDIRECT_URI: string;
    readonly VITE_DATAVERSE_URL: string;
    readonly VITE_DATAVERSE_URL_DEV?: string;
    readonly VITE_DYNAMICS_APP_ID?: string;
    readonly VITE_DYNAMICS_APP_ID_OPS?: string;
    readonly VITE_DYNAMICS_APP_ID_SALES?: string;
    readonly VITE_DATAVERSE_API_VERSION?: string;
    readonly VITE_ENABLE_DEV_TOOLS?: string;
    readonly VITE_LOG_LEVEL?: string;
    readonly VITE_MOCK_API?: string;
    readonly [key: string]: string | boolean | undefined;
  }

  interface ImportMeta {
    readonly env: ImportMetaEnv;
  }
}
